import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGetSessionsQuery, useDeleteSessionMutation } from '@/store/api/sessionsApi';
import {
  Loader2,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDebounce } from '@/hooks/useDebounce';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useNotifications } from '@/hooks/useNotifications';
import { Loader } from '@/components/common/Loader';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';

export function SessionsPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'Published' | 'Draft' | ''>('');
  const [take, setTake] = useState(10);
  const skip = (page - 1) * take;

  const {
    data: response,
    isLoading,
    error,
    isFetching,
  } = useGetSessionsQuery({
    search: debouncedSearch || undefined,
    skip,
    take,
    status: statusFilter || undefined,
  });
  const [deleteSession, { isLoading: isDeleting }] = useDeleteSessionMutation();
  const [sessionToDelete, setSessionToDelete] = useState<{ id: number; title: string } | null>(
    null
  );

  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, take]);

  const sessions = response?.data || [];
  const totalPages = response ? Math.ceil(response.count / take) : 0;

  const { success: notifySuccess, error: notifyError } = useNotifications();

  const confirmDelete = async () => {
    if (!sessionToDelete) return;
    try {
      await deleteSession(sessionToDelete.id).unwrap();
      notifySuccess('Session moved to trash.');
      setSessionToDelete(null);
    } catch (err: any) {
      notifyError(err?.data?.message || 'Failed to move session to trash. Please try again.');
    }
  };

  if (isLoading && !sessions.length) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorBanner
        type="error"
        title="Failed to load sessions"
        message="There was an error loading the sessions. Please try again."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sessions</h1>
          <p className="text-muted-foreground">
            Manage recorded talks and workshops for the portal.
          </p>
        </div>
        <Button asChild>
          <Link to="/sessions/create">
            <Plus className="mr-2 h-4 w-4" /> Create Session
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-4">
          <div>
            <CardTitle>All Sessions</CardTitle>
            <CardDescription>{response?.count ?? 0} sessions total</CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-center gap-2 mt-4 sm:mt-0 w-full sm:w-auto">
            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'Published' | 'Draft' | '')}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">All Statuses</option>
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
            {/* Items per page filter */}
            <select
              value={take}
              onChange={(e) => setTake(Number(e.target.value))}
              className="h-9 w-full sm:w-auto rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search title, speaker..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {isFetching && (
              <div className="absolute inset-0 bg-background/50 z-10 flex items-center justify-center rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {sessions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b">
                    <tr>
                      <th className="px-4 py-3 font-medium">Title</th>
                      <th className="px-4 py-3 font-medium">Tag</th>
                      <th className="px-4 py-3 font-medium">Speaker</th>
                      <th className="px-4 py-3 font-medium">Duration</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sessions.map((session) => (
                      <tr key={session.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-4 font-medium text-foreground max-w-xs">
                          <div className="truncate" title={session.title}>
                            {session.title}
                          </div>
                          <div className="text-xs text-muted-foreground font-normal">
                            {session.slug}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                            {session.tag}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-muted-foreground">{session.speaker}</td>
                        <td className="px-4 py-4 text-muted-foreground font-mono text-xs">
                          {session.duration}
                        </td>
                        <td className="px-4 py-4 text-muted-foreground whitespace-nowrap">
                          {session.date}
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant={session.status === 'Published' ? 'default' : 'warning'}>
                            {session.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {/* {session.youtubeUrl && (
                              <a
                                href={session.youtubeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open YouTube"
                              >
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </a>
                            )} */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => navigate(`/sessions/${session.id}/edit`)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() =>
                                setSessionToDelete({ id: session.id, title: session.title })
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p className="font-medium mb-2">No sessions found.</p>
                <p className="text-sm">
                  {searchTerm || statusFilter
                    ? 'Try adjusting your search or filter.'
                    : 'Create your first session to get started.'}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {response && response.count > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{skip + 1}</span> to{' '}
                <span className="font-medium">{Math.min(skip + take, response.count)}</span> of{' '}
                <span className="font-medium">{response.count}</span> results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <div className="text-sm font-medium">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal
        isOpen={!!sessionToDelete}
        onClose={() => setSessionToDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Move to Trash"
        description={`Are you sure you want to move "${sessionToDelete?.title}" to the trash? You can restore it later.`}
        confirmText="Move to Trash"
      />
    </div>
  );
}
